import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import Button from './Button';
import useBackButton from '../../hooks/useBackButton';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  loading?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'danger',
  loading = false,
}) => {
  useBackButton(onClose, isOpen, 150);
  const getIcon = () => {
    switch (type) {
      case 'danger':
        return <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />;
      case 'info':
        return <InformationCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />;
    }
  };

  const getConfirmButtonVariant = () => {
    switch (type) {
      case 'danger':
        return 'danger'; // Assuming Button component supports 'danger' or similar styling via className
      case 'warning':
        return 'primary';
      case 'info':
        return 'primary';
    }
  };

  const getConfirmButtonClass = () => {
     switch (type) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white';
      case 'warning':
        return 'bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-500 dark:hover:bg-yellow-600 text-white';
      case 'info':
        return 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white';
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-gray-900 rounded-2xl max-w-md w-full shadow-xl overflow-hidden border border-gray-100 dark:border-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start space-x-4">
                <div className={`flex-shrink-0 rounded-full p-2 ${
                  type === 'danger' ? 'bg-red-100 dark:bg-red-900/30' : 
                  type === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
                }`}>
                  {getIcon()}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {title}
                  </h3>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {message}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-6 flex gap-3">
                {type === 'danger' ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={onConfirm}
                      loading={loading}
                      className="w-1/2 !text-primary-600 !border-primary-200 dark:!border-primary-900/50 hover:!bg-primary-50 dark:hover:!bg-primary-950/30 whitespace-nowrap text-sm font-medium py-2.5"
                    >
                      {confirmText}
                    </Button>
                    <Button
                      onClick={onClose}
                      disabled={loading}
                      className="w-1/2 !bg-primary-500 hover:!bg-primary-600 !text-white font-medium whitespace-nowrap text-sm py-2.5 shadow-md shadow-primary-500/20"
                    >
                      {cancelText}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={onClose}
                      disabled={loading}
                      className="w-1/2 whitespace-nowrap text-sm py-2.5"
                    >
                      {cancelText}
                    </Button>
                    <Button
                      className={`w-1/2 ${getConfirmButtonClass()} whitespace-nowrap text-sm py-2.5`}
                      onClick={onConfirm}
                      loading={loading}
                    >
                      {confirmText}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ConfirmationModal;
